/* eslint-disable prettier/prettier */
/* prettier-ignore-file */
/* eslint-disable */
import axios from 'axios';
import { apiService } from './api.service';
import {
	UpdateProfileRequest,
	UpdateUserRoleRequest,
	CoachPerformanceStats,
	LearnerAchievements,
	ProgramRateItem,
	AdminDashboardAnalytics,
} from '../types/user';
import { sanitizeProfilePayload } from '../utils/sanitizeProfilePayload';

// ===============================
// Coach Course Dashboard Types
// ===============================

export interface CourseInfo {
	id: string;
	title: string;
	thumbnail?: string | null;
	status: string;
	currentenrollments: number;
	maxenrollments: number;
}

export interface EnrolledCoachee {
	userid: string;
	fullname: string;
	email: string;
	username?: string | null;
	profilepicture?: string | null;
	rolename: string;
	enrollmentid: string;
	enrolledat: string;
	enrollmentstatus: string;
	progress: number;
	lastaccessed?: string | null;
	completedat?: string | null;
	metadata?: {
		credentials?: string;
	};
}

export interface Invitee {
	id: string;
	email: string;
	fullname: string;
	invitetype: string;
	status: string;
	personalmessage?: string;
	expiresat?: string;
	createdat: string;
	lastresentat?: string;
	resendcount: number;
	acceptedat?: string;
}

export interface CourseDashboardView {
	course: CourseInfo;
	enrolledcoachees: EnrolledCoachee[];
	pendinginvitees: Invitee[];
	summary: {
		enrolled: number;
		pendinginvites: number;
		acceptedinvites: number;
		expiredinvites: number;
		completed: number;
		inprogress: number;
	};
}

export interface CoachCourseDashboard {
    courses: CourseDashboardView[];

    unenrolledusers?: {
        userid: string;
        fullname: string;
        email?: string;
        username?: string;
        profilepicture?: string;
        metadata?: {
            credentials?: string;
            [key: string]: unknown;
        };
        rolename: string;
    }[];

    totalsummary: {
        totalcourses: number;
        totalenrolled: number;
        totalpendinginvites: number;
    };
}

export interface LearnerProfile {
	id: string;
	fullname: string;
	email: string;
	username?: string | null;
	// add other fields as needed
	phonenumber?: string;
	dateofbirth?: string;
	gender?: string;
	profilepicture?: string;
	coverphoto?: string;
	bio?: string;
	location?: string;
	website?: string;
	isemailverified?: boolean;
	isphoneverified?: boolean;
	roleid?: string;
	accountstatus?: string;
	isactive?: boolean;
	issuspended?: boolean;
	lastloginat?: string;
	yearsofexperience?: number;
	areasofexpertise?: string[];
	languages?: string[];
	createdat?: string;
	updatedat?: string;
	tag?: string;
	metadata?: any;
	rolename?: string;
	hierarchylevel?: number;
	roledescription?: string;
	preferences?: Record<string, any>;
	settings?: Record<string, any>;
	certificationsCount?: number;
	organizationname?: string;
	designation?: string;
	skills?: string[];
	areasofinterest?: string[];
	linkedin?: string;
	instagram?: string;
	facebook?: string;
	coachingexperience?: string[];
	growthparameters?: string[];
	country?: string;
	timezone?: string;
}
export interface UserListItem {
	id: string;
	fullname: string;
	email: string;
	username?: string;
	profilepicture?: string;
	rolename?: string;
}
type ApiResponse<T> = {
	success: boolean;
	message?: string;
	data: T;
};

export interface UploadPictureResponse {
	success: boolean;
	message: string;
	data?: { path: string };
}

export interface CoachProfile {
	id: string;
	fullname: string;
	email?: string;
	username?: string;
	profilepicture?: string | null;
	bio?: string;
	yearsofexperience?: string;
	designation?: string;
	languages?: string[];
	location?: string;

	rolename: string;
	hierarchylevel: number;

	currentclients: number;
	avgrating: number;
	reviewcount: number;
	totalcoachingsessions: number;
	certificationscount: number;
	skillscount: number;
}
// ===============================
// Admin Dashboard Types
// ===============================

export interface AdminCourseInfo {
	id: string;
	title: string;
	thumbnail?: string | null;
	status: string;
	currentenrollments: number;
	maxenrollments: number;

	coach: {
		id: string;
		fullname: string;
		email: string;
		profilepicture?: string | null;
	};
}

export interface AdminInvitee {
	id: string;
	email: string;
	fullname: string;
	invitetype: string;
	status: string;
	personalmessage?: string;
	expiresat?: string;
	createdat: string;
	lastresentat?: string;
	resendcount: number;
	acceptedat?: string;

	course?: {
		id: string;
		title: string;
		thumbnail?: string | null;
	};
}

export interface AdminCourseDashboardView {
	course: AdminCourseInfo;
	enrolledcoachees: EnrolledCoachee[];
	pendinginvitees: AdminInvitee[];

	summary: {
		enrolled: number;
		pendinginvites: number;
		acceptedinvites: number;
		expiredinvites: number;
		completed: number;
		inprogress: number;
	};
}
// ===============================
// Admin Action Center Types
// ===============================

export interface AdminActionCenter {
	pendingapprovals: number;
	inactiveusers: number;
	opentickets: number;
	expiringinvites: number;
	pendingreviews: number;
	unassignedcoachees: number;
}

export interface AdminCourseDashboard {
	courses: AdminCourseDashboardView[];

	totalsummary: {
		totalcourses: number;
		totalcoaches: number;
		totallearners: number;
		totalenrolled: number;
		totalenrollments: number;
		totalpendinginvites: number;
		totalacceptedinvites: number;
		totalexpiredinvites: number;
	};
}
// Types (adjust if you already have these)
export interface AdminUserListFilters {
	search?: string;
	role?: string;
	accountstatus?: string;
	isactive?: boolean;
	hasprograms?: boolean;
	programrelationship?: string;
	page?: number;
	limit?: number;
}

export interface AdminUserListResponse {
	users: any[]; // replace with proper type if you have
	total: number;
	page: number;
	limit: number;
}

export interface LearnerDashboardCounts {
	courseid: string | null;

	coursetitle: string | null;

	summary: {
		resources: {
			total: number;
			downloaded : number;
			viewed: number;
			unviewed: number;
		};

		activities: {
			upcoming: number;
			attended: number;
			missed: number;
			total: number;
		};

		assignments: {
			due: number;
			overdue: number;
			completed: number;
			total: number;
		};
	};

	enrolledprograms: {
		id: string;
		title: string;
		status: string;
		progress: number;
	}[];
}

// ===============================
// Coach Action Center Types
// ===============================

export interface CoachActionCenter {
	pendingsubmissions: number;
	upcomingsessions: number;
	pendinginvites: number;
	unreadmessages: number;
}

// ===============================
// Coach Recent Activity Types
// ===============================

export interface CoachRecentActivityItem {
	id: string;

	activitytype: string;
	description: string;

	learnerid: string;
	learnername: string;

	resourcetype: string;
	resourceid: string;
	resourcetitle?: string;

	createdat: string;
}

export interface CoachRecentActivityResponse {
	items: CoachRecentActivityItem[];
	total: number;
	page: number;
	limit: number;
}

// ===============================
// Admin Recent Activity Types
// ===============================

export interface AdminRecentActivityItem {
	id: string;
	activitytype: string;
	description: string;

	actorid: string;
	actorname: string;

	resourcetype: string;
	resourceid: string;
	resourcetitle?: string;

	createdat: string;
}

export interface AdminRecentActivityResponse {
	items: AdminRecentActivityItem[];
	total: number;
	page: number;
	limit: number;
}

// ===============================
// Coach Learner Overview Types
// ===============================

export interface LearnerAttendanceRow {
	userid: string;
	fullname: string;
	email: string;
	profilepicture?: string | null;

	courseid: string;
	coursetitle: string;

	totalsessions: number;
	attendedsessions: number;
	missedsessions: number;
	attendancerate: number;
}

export interface LearnerAssignmentRow {
	userid: string;
	fullname: string;
	email: string;
	profilepicture?: string | null;

	courseid: string;
	coursetitle: string;

	totalassignments: number;
	submitted: number;
	passed: number;
	completionrate: number;
}

export interface CoachLearnerOverview {
	coachid: string;

	summary: {
		totallearners: number;
		totalenrollments: number;
		upcomingsessions: number;
	};

	attendance: LearnerAttendanceRow[];

	assignments: LearnerAssignmentRow[];

	pagination: {
		page: number;
		limit: number;
		total: number;
	};
}
const api = process.env.REACT_APP_BASE_API_URL!;

export const userService = {
	getUserProfile: async () => {
		try {
			const res = await apiService.get<{
				success: boolean;
				data: LearnerProfile;
				message?: string;
			}>('/user/profile');

			// Handle 304 manually (apiService already returns res.data)
			if (!res) {
				const cached = localStorage.getItem('cachedProfile');
				return cached ? JSON.parse(cached) : null;
			}

			if (res.success && res.data) {
				localStorage.setItem('cachedProfile', JSON.stringify(res.data));
				return res;
			}

			throw new Error(res.message || 'Invalid response structure');
		} catch (error: any) {
			throw new Error(error.message || 'Failed to fetch user profile');
		}
	},

	getUserProfileById: async (
		userId: string
	): Promise<LearnerProfile | null> => {
		try {
			const res = await apiService.get(`/user/${userId}`);

			// 🔹 Check if res.data is already a LearnerProfile
			const user = res.data as LearnerProfile;

			if (!user || !user.id) {
				throw new Error('No profile data found for this ID');
			}

			// convert dates to ISO strings
			return {
				...user,
				dateofbirth: user.dateofbirth
					? new Date(user.dateofbirth).toISOString()
					: undefined,
				lastloginat: user.lastloginat
					? new Date(user.lastloginat).toISOString()
					: undefined,
				createdat: user.createdat
					? new Date(user.createdat).toISOString()
					: undefined,
				updatedat: user.updatedat
					? new Date(user.updatedat).toISOString()
					: undefined,
				organizationname: user.organizationname || '',
				designation: user.designation || '',
				skills: user.skills || [],
				areasofinterest: user.areasofinterest || [],
			};
		} catch (error: any) {
			throw new Error(
				error.message || 'Failed to fetch user profile by ID'
			);
		}
	},

	deleteUser: async (
		userId: string,
		reason?: string
	): Promise<{ success: boolean; message: string }> => {
		try {
			const token = localStorage.getItem('token'); // if your API needs auth

			const res = await axios.delete(`${api}/user/${userId}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: { reason }, // send reason in body
			});

			return res.data; // { success, message }
		} catch (err: any) {
			console.error('Error in userService.deleteUser:', err);
			return {
				success: false,
				message: err.message || 'Failed to delete user',
			};
		}
	},

	updateUserProfile: async (data: UpdateProfileRequest) => {
		try {
			// 🔹 API call using apiService
			const safeData = sanitizeProfilePayload(data);

			const res = await apiService.put<{
				success: boolean;
				message: string;
				data?: LearnerProfile;
			}>('/user/profile', safeData);

			// 🔹 Update cache instantly if success
			if (res.success) {
				if (res.data) {
					localStorage.setItem(
						'cachedProfile',
						JSON.stringify(res.data)
					);
				}
				return res;
			}

			throw new Error(res.message || 'Invalid response structure');
		} catch (error: any) {
			console.error('Error in userService.updateUserProfile:', error);
			return { success: false, message: error.message };
		}
	},

	// userService
	downloadFile: async (fileUrl: string) => {
		try {
			const token = localStorage.getItem('token');

			const response = await axios.get(fileUrl, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			// response.data.data.picture is already a base64 URL
			return { success: true, base64: response.data.data.picture };
		} catch (error: any) {
			return { success: false, message: error.message };
		}
	},

	uploadProfilePicture: async (
		file: File,
		type: 'profilepicture' | 'coverphoto' = 'profilepicture'
	) => {
		try {
			const token = localStorage.getItem('token');
			const formData = new FormData();
			formData.append('picture', file);

			const url = `${api}/user/profile/upload?type=${type}`;

			const res = await axios.post(url, formData, {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
			});

			return res.data; // { success, message, data }
		} catch (err: any) {
			return { success: false, message: err.message };
		}
	},

	searchUsers: async (
		query: string,
		filters?: { role?: string; limit?: number }
	): Promise<UserListItem[]> => {
		const response = await apiService.get<{
			success: boolean;
			data: UserListItem[];
			message?: string;
		}>('/search', {
			q: query,
			role: filters?.role,
			limit: filters?.limit || 20,
		});

		// ⭐ unwrap because apiService returns axios response
		const res = (response as any).data ?? response;

		if (!res.success) {
			throw new Error(res.message || 'Search failed');
		}

		return res.data;
	},

	getCoachCourseDashboard: async (filters?: {
		courseId?: string;
		inviteStatus?: string;
		search?: string;
		page?: number;
		limit?: number;
	}): Promise<CoachCourseDashboard> => {
		// ✅ Return type specified
		try {
			const res = await apiService.get('/user/courseattendees', {
				params: {
					courseId: filters?.courseId,
					inviteStatus: filters?.inviteStatus,
					search: filters?.search,
					page: filters?.page || 1,
					limit: filters?.limit || 50,
				},
			});

			const response = res.data; // backend returns courses + totalsummary

			return response as CoachCourseDashboard; // ✅ cast to proper type
		} catch (error: any) {
			console.error('API error', error.response || error.message);
			throw new Error(
				error.message || 'Failed to fetch course dashboard'
			);
		}
	},

	getAdminDashboard: async (filters?: {
		coachId?: string;
		courseId?: string;
		inviteStatus?: string;
		search?: string;
		page?: number;
		limit?: number;
	}): Promise<AdminCourseDashboard> => {
		try {
			const res = (await apiService.get('/user/admindashboard', {
				params: {
					coachId: filters?.coachId,
					courseId: filters?.courseId,
					inviteStatus: filters?.inviteStatus,
					search: filters?.search,
					page: filters?.page || 1,
					limit: filters?.limit || 50,
				},
			})) as ApiResponse<AdminCourseDashboard>; // ✅ FIX HERE

			if (!res.success) {
				throw new Error(
					res.message || 'Failed to fetch admin dashboard'
				);
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Admin dashboard API error:',
				error.response || error.message
			);
			throw new Error(error.message || 'Failed to fetch admin dashboard');
		}
	},

	updateUserById: async (userId: string, data: UpdateUserRoleRequest) => {
		try {
			const res = await apiService.put<{
				success: boolean;
				message: string;
			}>(`/user/${userId}`, data); // ✅ send directly

			if (res.success) {
				return res;
			}

			throw new Error(res.message || 'Failed to update user');
		} catch (error: any) {
			console.error('Error in updateUserById:', error);
			return { success: false, message: error.message };
		}
	},

	getAdminUserList: async (
		filters?: AdminUserListFilters
	): Promise<AdminUserListResponse> => {
		try {
			const res = (await apiService.get('/user/userlist?limit=100', {
				params: {
					search: filters?.search,
					role: filters?.role,
					accountstatus: filters?.accountstatus,
					isactive: filters?.isactive,
					hasprograms: filters?.hasprograms,
					programrelationship: filters?.programrelationship,
					page: filters?.page || 1,
					limit: filters?.limit || 100,
				},
			})) as ApiResponse<AdminUserListResponse>;

			if (!res.success) {
				throw new Error(res.message || 'Failed to fetch user list');
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Admin user list API error:',
				error.response || error.message
			);
			throw new Error(error.message || 'Failed to fetch user list');
		}
	},

	deleteCoach: async (
		coachId: string,
		reason?: string
	): Promise<{ success: boolean; message: string }> => {
		try {
			const token = localStorage.getItem('token');

			const res = await axios.delete(`${api}/user/${coachId}/coach`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: { reason }, // ✅ important (DELETE body)
			});

			return res.data; // { success, message }
		} catch (err: any) {
			console.error('Error in userService.deleteCoach:', err);
			return {
				success: false,
				message: err.message || 'Failed to delete coach',
			};
		}
	},

	getCoachPerformanceStats: async (
	coachId: string
): Promise<any> => {
	try {
		const res = await apiService.get(
			`/user/${coachId}/performance`
		);

		console.log('FULL RESPONSE:', res);
		console.log('TYPE OF RESPONSE:', typeof res);

		return res;
	} catch (error) {
		console.error(error);
		throw error;
	}
},
	getLearnerAchievements: async (
		learnerId: string
	): Promise<LearnerAchievements> => {
		try {
			const res = await apiService.get<{
				success: boolean;
				data: LearnerAchievements;
				message?: string;
			}>(`/user/${learnerId}/achievements`);

			// ⚠️ same pattern you used elsewhere
			const response = (res as any).data ?? res;

			if (!response.success) {
				throw new Error(
					response.message || 'Failed to fetch learner achievements'
				);
			}

			return response.data;
		} catch (error: any) {
			console.error('Error in getLearnerAchievements:', error);
			throw new Error(
				error.message || 'Failed to fetch learner achievements'
			);
		}
	},
	getAdminDashboardAnalytics: async (): Promise<AdminDashboardAnalytics> => {
		try {
			const res = (await apiService.get(
				'/user/admin/dashboard/analytics'
			)) as ApiResponse<AdminDashboardAnalytics>;

			if (!res.success) {
				throw new Error(res.message || 'Failed to fetch analytics');
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Analytics API error:',
				error.response || error.message
			);
			throw new Error(error.message || 'Failed to fetch analytics');
		}
	},

	async getCoaches(
		type: 'coach' | 'mentor' = 'coach',
		filters?: {
			areasofexpertise?: string[];
			languages?: string[];
			minrating?: number;
			availability?: 'available' | 'limited' | 'full';
		}
	): Promise<CoachProfile[]> {
		try {
			const params: any = {
				type,
			};

			if (filters?.areasofexpertise?.length) {
				params.areasofexpertise = filters.areasofexpertise.join(',');
			}

			if (filters?.languages?.length) {
				params.languages = filters.languages.join(',');
			}

			if (filters?.minrating) {
				params.minrating = filters.minrating;
			}

			if (filters?.availability) {
				params.availability = filters.availability;
			}

			console.log('📡 Fetch Coaches Params:', params);

			const res = await apiService.get<CoachProfile[]>(
				'/user/coaches',
				params
			);

			if (!res.success) {
				throw new Error(res.message || 'Failed to fetch coaches');
			}

			console.log('✅ Coaches API Response:', res.data);

			return res.data || [];
		} catch (error) {
			console.error('❌ getCoaches error:', error);
			throw error;
		}
	},
	// async getLearnerDashboardCounts(): Promise<LearnerDashboardCounts> {
	//       try {
	//           const response =
	//               await apiService.get<LearnerDashboardCounts>(
	//                   '/user/dashboard/counts'
	//               );

	//           if (!response.success) {
	//               throw new Error(response.message);
	//           }

	//           return response.data;
	//       } catch (error: any) {
	//           console.error('Dashboard counts fetch error:', error);

	//           throw new Error(
	//               error.message || 'Failed to fetch dashboard counts'
	//           );
	//       }
	//   },
	async getLearnerDashboardCounts(
		courseid?: string
	): Promise<LearnerDashboardCounts> {
		try {
			console.log('Sending courseid:', courseid);

			const response =
				// await apiService.get<LearnerDashboardCounts>(
				//     '/user/dashboard/counts',
				//     {
				//         params: {
				//             courseid,
				//         },
				//     }
				// );
				await apiService.get<LearnerDashboardCounts>(
					'/user/dashboard/counts',
					{
						courseid,
					}
				);

			if (!response.success) {
				throw new Error(response.message);
			}

			return response.data;
		} catch (error: any) {
			console.error('Dashboard counts fetch error:', error);

			throw new Error(
				error.message || 'Failed to fetch dashboard counts'
			);
		}
	},

	// ===============================
	// Admin Action Center
	// ===============================

	getAdminActionCenter: async (): Promise<AdminActionCenter> => {
		try {
			const res = (await apiService.get(
				'/user/admin/action-center'
			)) as ApiResponse<AdminActionCenter>;

			if (!res.success) {
				throw new Error(
					res.message || 'Failed to fetch admin action center'
				);
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Admin Action Center API Error:',
				error.response || error.message
			);

			throw new Error(
				error.message || 'Failed to fetch admin action center'
			);
		}
	},

	// ===============================
	// Admin Recent Activity
	// ===============================

	getAdminRecentActivity: async (
		page = 1,
		limit = 20
	): Promise<AdminRecentActivityResponse> => {
		try {
			const res = (await apiService.get('/user/admin/recent-activity', {
				page,
				limit,
			})) as ApiResponse<AdminRecentActivityResponse>;

			if (!res.success) {
				throw new Error(
					res.message || 'Failed to fetch recent activity'
				);
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Admin Recent Activity API Error:',
				error.response || error.message
			);

			throw new Error(error.message || 'Failed to fetch recent activity');
		}
	},

	// ===============================
	// Coach Action Center
	// ===============================

	getCoachActionCenter: async (): Promise<CoachActionCenter> => {
		try {
			const res = (await apiService.get(
				'/user/coach/action-center'
			)) as ApiResponse<CoachActionCenter>;

			if (!res.success) {
				throw new Error(
					res.message || 'Failed to fetch coach action center'
				);
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Coach Action Center API Error:',
				error.response || error.message
			);

			throw new Error(
				error.message || 'Failed to fetch coach action center'
			);
		}
	},

	// ===============================
	// Coach Recent Activity
	// ===============================

	getCoachRecentActivity: async (
		page = 1,
		limit = 20
	): Promise<CoachRecentActivityResponse> => {
		try {
			const res = (await apiService.get('/user/coach/recent-activity', {
				page,
				limit,
			})) as ApiResponse<CoachRecentActivityResponse>;

			if (!res.success) {
				throw new Error(
					res.message || 'Failed to fetch coach recent activity'
				);
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Coach Recent Activity API Error:',
				error.response || error.message
			);

			throw new Error(
				error.message || 'Failed to fetch coach recent activity'
			);
		}
	},

	// ===============================
	// Coach Learner Overview
	// ===============================

	getCoachLearnerOverview: async (
		courseid?: string,
		page = 1,
		limit = 50
	): Promise<CoachLearnerOverview> => {
		try {
			const res = (await apiService.get('/user/coach/learner-overview', {
				courseid,
				page,
				limit,
			})) as ApiResponse<CoachLearnerOverview>;

			if (!res.success) {
				throw new Error(
					res.message || 'Failed to fetch learner overview'
				);
			}

			return res.data;
		} catch (error: any) {
			console.error(
				'Coach Learner Overview API Error:',
				error.response || error.message
			);

			throw new Error(
				error.message || 'Failed to fetch learner overview'
			);
		}
	},
};
